/**
 * Scenario 全体の Zod スキーマ（Worker / Player バリデーションの土台）
 *
 * 設計方針: 「核だけ厳格・周辺は通す」。
 * - 核（nodes → steps → StepSchema）は判別ユニオンで厳格に検証する。
 *   ここはランタイムが必ず読む構造であり、壊れていれば再生不能になるため。
 * - startScreen / clues / ui / routeMap / meta / startNodeId 周辺の補助フィールドは
 *   `z.unknown()` 等で通す。これらは TypeScript interface（scenario.ts）側で型付け済みで、
 *   ここで二重に厳格化すると本番モック（demo_scenario_*.json）を誤って弾くリスクがある。
 *
 * 既存資産との関係:
 * - フィールド名は scenario.ts の `ScenarioContent` / `ScenarioNode` interface に一致させる。
 * - StepSchema（stepSchema.ts）・ThemeSchema（theme.ts）・ScenarioVariablesSchema
 *   （variableSchema.ts）の完成済み Zod を再利用する。
 * - 既存の TS interface 名（ScenarioContent / ScenarioNode）とは衝突しない別名で export する。
 */
import { z } from 'zod';
import { StepSchema } from './stepSchema';
import { ThemeSchema, MoodSchema } from './theme';
import { ScenarioVariablesSchema } from './variableSchema';
/**
 * シナリオノード。`steps` のみ StepSchema で厳格に検証する。
 * その他（title / nextNodeId / meta）は scenario.ts の ScenarioNode に合わせて緩く通す。
 *
 * `mood` は P8-4 で追加した node 単位の mood override（optional・minor・schemaVersion 1 据置）。
 * 値は既存の MoodSchema（7種）を再利用し、語彙の二重管理をしない。
 */
export const ScenarioNodeSchema = z.object({
    id: z.string().min(1),
    title: z.string().nullish(),
    steps: z.array(StepSchema),
    nextNodeId: z.string().nullish(),
    mood: MoodSchema.nullish(),
    meta: z.record(z.unknown()).optional(),
});
/**
 * シナリオ全体。
 *
 * 厳格に検証するフィールド:
 * - nodes（→ steps → StepSchema 判別ユニオン）: ランタイムの核。
 * - theme: ThemeSchema（Phase D / 凍結対象。mood enum を含む）。
 * - variables: ScenarioVariablesSchema（変数定義の核）。
 *
 * 緩く通すフィールド（z.unknown()）: scenario.ts で型付け済みの補助設定。
 * - meta / startScreen / clues / ui / routeMap
 *
 * schemaVersion は scenario.ts では `number | null`。validate-scenario.mjs 側が
 * 別途 `=== 1` を強制しているため、ここでは弾かない（number/null/undefined を許容）。
 */
export const ScenarioSchema = z.object({
    schemaVersion: z.number().nullish(),
    startNodeId: z.string().nullable(),
    nodes: z.array(ScenarioNodeSchema),
    theme: ThemeSchema.optional(),
    variables: ScenarioVariablesSchema.optional(),
    // ── 以下は周辺フィールド（scenario.ts で型付け済み）。過剰検証を避けて通す ──
    meta: z.unknown().optional(),
    startScreen: z.unknown().optional(),
    clues: z.unknown().optional(),
    ui: z.unknown().optional(),
    routeMap: z.unknown().optional(),
});
//# sourceMappingURL=scenario.schema.js.map